import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from 'hooks/useAuth';
import { reportEmergency } from 'services/api';
import { getUserLocation, playAlertSound, updateTabTitle } from 'utils/helpers';
import { MOCK_INCIDENTS, MOCK_SAFE_ZONES, INCIDENT_CATEGORIES, SEVERITY_LEVELS, DISPATCH_STATUS_CONFIG, MOCK_RESPONDERS } from 'utils/mockData';
import { useTranslation } from 'react-i18next';
import IncidentMap from 'components/common/IncidentMap';
import ConfidenceMeter from 'components/common/ConfidenceMeter';
import VoiceCommand from 'components/common/VoiceCommand';
import DispatchChain from 'components/common/DispatchChain';
import toast from 'react-hot-toast';
import { formatTimeAgo } from 'utils/helpers';

// ─── SOS MODAL ───────────────────────────────────────────────────────────────
function SOSModal({ onClose }) {
  const [step, setStep] = useState('category'); // category | subcategory | details | sent
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedSub, setSelectedSub] = useState('');
  const [description, setDescription] = useState('');
  const [aiSeverity, setAiSeverity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(12);
  const [incidentId, setIncidentId] = useState(null);
  const { user } = useAuth();

  const detectSeverity = async (cat, sub, desc) => {
    // AI severity detection based on category + subcategory + keywords
    const criticalKeywords = ['child', 'kidnap', 'cardiac', 'no pulse', 'explosion', 'trapped', 'not breathing', 'unconscious'];
    const highKeywords = ['riot', 'flood', 'fire', 'accident', 'injury', 'robbery', 'violence'];
    const text = `${sub} ${desc}`.toLowerCase();
    const isCritical = criticalKeywords.some((k) => text.includes(k));
    const isHigh = highKeywords.some((k) => text.includes(k)) || ['police', 'flood', 'fire'].includes(cat);
    if (cat === 'police' && (sub === 'Kidnapping' || sub === 'Riot / Hangama')) return 'critical';
    if (isCritical) return 'critical';
    if (isHigh) return 'high';
    return 'medium';
  };

  const handleSubmit = async () => {
    setLoading(true);
    playAlertSound();
    updateTabTitle('alert');
    const sev = await detectSeverity(selectedCat, selectedSub, description);
    setAiSeverity(sev);

    try {
      const location = await getUserLocation().catch(() => ({ lat: 20.2961, lng: 85.8245 }));
      const result = await reportEmergency({
        type: selectedCat,
        subcategory: selectedSub,
        description,
        severity: sev,
        location,
        userId: user?.uid || 'anonymous',
        timestamp: new Date().toISOString(),
      }).catch(() => ({ data: { incidentId: 'INC-' + Math.random().toString(36).slice(2,8).toUpperCase() } }));

      setIncidentId(result.data?.incidentId || 'INC-' + Math.random().toString(36).slice(2,8).toUpperCase());
      setStep('sent');
      toast.success('Emergency dispatched! Responders notified.', { icon: '🚨' });

      const timer = setInterval(() => {
        setCountdown((c) => { if (c <= 1) { clearInterval(timer); return 0; } return c - 1; });
      }, 1000);
    } catch {
      setStep('sent');
    } finally {
      setLoading(false);
    }
  };

  const cat = INCIDENT_CATEGORIES[selectedCat] || {};
  const sevCfg = SEVERITY_LEVELS[aiSeverity] || {};

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(10,15,30,0.92)', backdropFilter: 'blur(10px)' }}
    >
      <motion.div
        initial={{ scale: 0.88, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.88, y: 30 }}
        transition={{ type: 'spring', stiffness: 280, damping: 24 }}
        className="glass-card w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto"
        style={{ borderColor: cat.border || '#FF3B3B40' }}
      >
        <button onClick={() => { onClose(); updateTabTitle('normal'); }} className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl">✕</button>

        {/* Step 1: Category */}
        {step === 'category' && (
          <>
            <h2 className="font-display font-bold text-xl text-white text-center mb-1">🚨 Emergency Type</h2>
            <p className="text-gray-400 text-sm text-center mb-5">Select the category that best matches</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(INCIDENT_CATEGORIES).map(([key, cfg]) => (
                <motion.button
                  key={key}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => { setSelectedCat(key); setStep('subcategory'); }}
                  className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 transition-all"
                  style={{ borderColor: cfg.border, background: cfg.bg }}
                >
                  <span className="text-3xl">{cfg.icon}</span>
                  <span className="font-bold text-sm" style={{ color: cfg.color }}>{cfg.label}</span>
                </motion.button>
              ))}
            </div>
          </>
        )}

        {/* Step 2: Subcategory */}
        {step === 'subcategory' && cat && (
          <>
            <button onClick={() => setStep('category')} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4">← Back</button>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">{cat.icon}</span>
              <h2 className="font-display font-bold text-lg" style={{ color: cat.color }}>{cat.label} — What happened?</h2>
            </div>
            <div className="space-y-2">
              {cat.subcategories?.map((sub) => (
                <motion.button
                  key={sub}
                  whileHover={{ x: 4 }}
                  onClick={() => { setSelectedSub(sub); setStep('details'); }}
                  className="w-full text-left px-4 py-3 rounded-xl border transition-all text-sm font-semibold text-white hover:border-gray-400"
                  style={{ borderColor: cat.border, background: cat.bg }}
                >
                  {sub}
                </motion.button>
              ))}
              <motion.button
                whileHover={{ x: 4 }}
                onClick={() => { setSelectedSub('Other'); setStep('details'); }}
                className="w-full text-left px-4 py-3 rounded-xl border border-navy-border text-sm text-gray-400 hover:text-white hover:border-gray-400"
              >
                Other / Not listed
              </motion.button>
            </div>
          </>
        )}

        {/* Step 3: Description + AI severity */}
        {step === 'details' && (
          <>
            <button onClick={() => setStep('subcategory')} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4">← Back</button>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">{cat.icon}</span>
              <div>
                <div className="font-bold text-sm" style={{ color: cat.color }}>{cat.label}</div>
                <div className="text-xs text-gray-400">{selectedSub}</div>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-3 mt-3">Briefly describe what you see (optional but helps AI)</p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`Describe the ${selectedSub.toLowerCase()} situation...`}
              rows={3}
              className="w-full bg-navy border border-navy-border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-electric resize-none mb-4"
            />
            <div className="p-3 rounded-xl bg-electric/5 border border-electric/20 mb-4">
              <div className="text-xs font-mono text-electric mb-1">🧠 AI Severity Detection</div>
              <div className="text-xs text-gray-300">Based on category "{cat.label}" + subcategory "{selectedSub}", AI will auto-classify severity before dispatch.</div>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-display font-bold text-navy flex items-center justify-center gap-2"
              style={{ background: cat.color }}
            >
              {loading ? <><div className="spinner w-4 h-4" /> Dispatching...</> : `🚨 Send Emergency Alert`}
            </motion.button>
          </>
        )}

        {/* Step 4: Sent */}
        {step === 'sent' && (
          <div className="text-center py-4">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300 }} className="text-5xl mb-4">✅</motion.div>
            <h2 className="font-display font-bold text-xl text-emerald mb-1">Alert Dispatched!</h2>
            <p className="text-gray-400 text-sm mb-3">Responders notified</p>

            {aiSeverity && (
              <div className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: sevCfg.bg, border: `1px solid ${sevCfg.color}40` }}>
                <span className="font-mono text-xs font-bold" style={{ color: sevCfg.color }}>🧠 AI Severity: {sevCfg.label}</span>
              </div>
            )}

            <div className="p-3 rounded-xl bg-electric/10 border border-electric/20 mb-4">
              <div className="text-xs font-mono text-electric mb-1">Incident ID</div>
              <div className="font-bold text-white text-sm">{incidentId}</div>
            </div>

            <div className="text-xs text-gray-500 mb-4">2 nearest responders notified simultaneously · Primary has 30s to respond</div>

            <div className="w-full bg-navy-border rounded-full h-1.5 overflow-hidden mb-3">
              <motion.div className="h-full bg-emerald rounded-full" initial={{ width: '100%' }} animate={{ width: `${(countdown / 12) * 100}%` }} transition={{ duration: 1 }} />
            </div>
            <p className="text-xs text-gray-400">First responder will arrive in approximately <span className="text-emerald font-bold">{countdown}</span> minutes</p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── LIVE STATUS TRACKER ─────────────────────────────────────────────────────
function LiveStatusCard({ incident }) {
  const cat = INCIDENT_CATEGORIES[incident.type] || {};
  const sev = SEVERITY_LEVELS[incident.severity] || {};
  const primaryDispatch = incident.dispatchChain?.[0];
  const primaryResponder = primaryDispatch
    ? MOCK_RESPONDERS.find((r) => r.id === primaryDispatch.responderId)
    : null;
  const dispatchCfg = DISPATCH_STATUS_CONFIG[primaryDispatch?.status] || {};

  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      className="p-4 rounded-xl border"
      style={{ borderColor: cat.border, background: cat.bg }}
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl flex-shrink-0">{cat.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-bold text-sm text-white">{incident.subcategory || incident.type}</span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: sev.bg, color: sev.color }}>{sev.label}</span>
          </div>
          <div className="text-xs text-gray-400 mb-2 truncate">📍 {incident.location.address}</div>

          {/* Dispatch status */}
          {primaryDispatch && (
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: dispatchCfg.color }}>
                {dispatchCfg.icon} {dispatchCfg.label}
              </span>
              {primaryResponder && (
                <span className="text-xs text-gray-400">· {primaryResponder.name} · ETA {primaryResponder.eta}</span>
              )}
            </div>
          )}
        </div>
        <ConfidenceMeter score={incident.verificationScore} size={40} label="" />
      </div>
    </motion.div>
  );
}

// ─── MAIN DASHBOARD ──────────────────────────────────────────────────────────
export default function CitizenDashboard() {
  const { user, userProfile } = useAuth();
  const { t } = useTranslation();
  const [showSOS, setShowSOS] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const myAlerts = MOCK_INCIDENTS.filter((i) => i.status !== 'resolved');

  useEffect(() => {
    getUserLocation().then(setUserLocation).catch(() => setUserLocation({ lat: 20.2961, lng: 85.8245 }));
  }, []);

  const handleVoiceTranscript = (text) => {
    toast(`Voice report received: "${text.slice(0, 40)}..."`, { icon: '🎙️' });
    setShowSOS(true);
  };

  return (
    <div className="min-h-screen bg-navy pb-24">
      {/* Header */}
      <div className="px-4 py-4 flex items-center justify-between border-b border-navy-border">
        <div>
          <h1 className="font-display font-bold text-base text-white">Citizen Safety Portal</h1>
          <p className="text-xs text-gray-400">
            📍 {userLocation ? 'Bhubaneswar, Odisha' : 'Detecting location...'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <VoiceCommand onTranscript={handleVoiceTranscript} />
          <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald/10 border border-emerald/30">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald animate-pulse" />
            <span className="text-xs font-mono text-emerald">Online</span>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* SOS Hero */}
        <div className="text-center py-6">
          <p className="text-xs font-mono text-gray-400 tracking-widest mb-5">IN CASE OF EMERGENCY — PRESS SOS</p>
          <div className="relative inline-block mb-5">
            <div className="sos-ring" style={{ width: 120, height: 120 }} />
            <div className="sos-ring" style={{ width: 120, height: 120 }} />
            <div className="sos-ring" style={{ width: 120, height: 120 }} />
            <motion.button
              whileHover={{ scale: 1.07 }}
              whileTap={{ scale: 0.94 }}
              onClick={() => setShowSOS(true)}
              className="relative w-28 h-28 rounded-full font-display font-black text-2xl text-white shadow-glow-red z-10"
              style={{ background: 'linear-gradient(135deg, #FF3B3B, #CC2F2F)' }}
              aria-label="Activate SOS Emergency Alert"
            >
              SOS
            </motion.button>
          </div>

          {/* Category quick buttons */}
          <div className="flex gap-2 justify-center flex-wrap">
            {Object.entries(INCIDENT_CATEGORIES).map(([key, cfg]) => (
              <motion.button
                key={key}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setShowSOS(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
                style={{ borderColor: cfg.border, background: cfg.bg, color: cfg.color }}
              >
                <span className="text-base">{cfg.icon}</span>
                {cfg.label}
              </motion.button>
            ))}
          </div>
          <p className="text-gray-500 text-xs mt-3">
            Hold <kbd className="px-1 py-0.5 rounded bg-navy-border text-gray-300 font-mono text-xs">Space</kbd> for voice · AI detects severity automatically
          </p>
        </div>

        {/* Live Incident Map */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-sm text-white">🗺️ Live Incident Map</h2>
            <span className="text-xs font-mono text-gray-400">{myAlerts.length} active near you</span>
          </div>
          <IncidentMap incidents={myAlerts} userLocation={userLocation} height="280px" />
        </div>

        {/* My Alerts + Dispatch Status */}
        <div>
          <h2 className="font-display font-bold text-sm text-white mb-3">🔔 Nearby Incidents & Responder Status</h2>
          <div className="space-y-3">
            {myAlerts.slice(0, 4).map((inc) => (
              <div key={inc.id}>
                <LiveStatusCard incident={inc} />
                <button
                  onClick={() => setSelectedIncident(selectedIncident?.id === inc.id ? null : inc)}
                  className="w-full text-xs text-electric py-1.5 hover:text-white transition-colors"
                >
                  {selectedIncident?.id === inc.id ? '▲ Hide dispatch chain' : '▼ View dispatch chain & responders'}
                </button>
                <AnimatePresence>
                  {selectedIncident?.id === inc.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pb-3">
                        <DispatchChain incident={inc} currentResponderId={null} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>

        {/* Safe Zones */}
        <div className="glass-card p-5">
          <h2 className="font-display font-bold text-sm text-white mb-4">🛡️ Nearby Safe Zones</h2>
          <div className="space-y-3">
            {MOCK_SAFE_ZONES.map((sz) => {
              const pct = Math.round((sz.occupancy / sz.capacity) * 100);
              return (
                <div key={sz.id} className="p-3 rounded-lg bg-navy border border-navy-border">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-semibold text-sm text-white">{sz.name}</div>
                      <div className="text-xs text-gray-400">{sz.distance} · {sz.occupancy}/{sz.capacity} capacity</div>
                    </div>
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${sz.lat},${sz.lng}`} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1 rounded-lg bg-electric/10 border border-electric/30 text-electric text-xs font-semibold hover:bg-electric/20">
                      Navigate
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-navy-border rounded-full h-1.5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct > 80 ? '#FF3B3B' : pct > 50 ? '#FFB300' : '#00C896' }} />
                    </div>
                    <span className="text-xs font-mono text-gray-400">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showSOS && <SOSModal onClose={() => setShowSOS(false)} />}
      </AnimatePresence>
    </div>
  );
}
